import type { UserRole } from '@/types';

export type PermissionFlags = {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
};

export type PermissionModule = {
    id: string;
    key: string;
    name: string;
    category?: string;
    description?: string;
    routes?: string[];
    hidden_routes?: string[];
    field_restrictions?: string[];
    approval_rules?: Array<{
        action_key: string;
        required_role: string;
        description: string;
    }>;
};

export type RolePermissionProfile = {
    role_id: string;
    role_name: string;
    summary?: string;
    locked?: boolean;
    hidden_routes?: string[];
    field_restrictions?: string[];
    approval_rules?: Array<{
        action_key: string;
        required_role: string;
        description: string;
    }>;
    permissions: Record<string, PermissionFlags>;
};

export type AccessPermissionsPayload = {
    modules: PermissionModule[];
    roles: RolePermissionProfile[];
};

export const institutePortalRoles = new Set<UserRole>([
    'institute_admin',
    'principal',
    'instructor',
    'senior_instructor',
    'admin_staff',
    'accountant',
    'hr',
    'receptionist',
    'counselor',
    'operations',
]);

// The institute OWNER role always has full access and is never filtered by the
// permission catalog (so it can never be locked out of its own institute).
// Every OTHER institute role is gated by the catalog — and when the catalog is
// not yet known, access is DENIED (fail closed), so no page ever leaks to a
// role that shouldn't see it while permissions are still loading or if the
// fetch failed.
const ownerRoles = new Set<UserRole>(['institute_admin']);

function normalizePath(pathname: string) {
    return pathname.replace(/\/+$/, '') || '/';
}

function resolveModuleForPath(modules: PermissionModule[], pathname: string) {
    const normalizedPath = normalizePath(pathname);
    let bestMatch: PermissionModule | null = null;
    let bestLength = -1;

    for (const module of modules) {
        const candidates = [...(module.routes || []), ...(module.hidden_routes || [])];
        for (const candidate of candidates) {
            const normalizedCandidate = normalizePath(candidate);
            const matches = normalizedPath === normalizedCandidate || normalizedPath.startsWith(`${normalizedCandidate}/`);
            if (matches && normalizedCandidate.length > bestLength) {
                bestMatch = module;
                bestLength = normalizedCandidate.length;
            }
        }
    }

    return bestMatch;
}

export function canRoleAccessPath(payload: AccessPermissionsPayload | null, role: UserRole | undefined, pathname: string) {
    if (!role || !institutePortalRoles.has(role)) return true;
    if (ownerRoles.has(role)) return true;                 // institute owner: full access
    // Permissions unknown (loading / fetch failed) → allow only non-admin paths
    // so a staff role can't reach an /admin page it may not be entitled to.
    if (!payload) return !normalizePath(pathname).startsWith('/admin');

    const module = resolveModuleForPath(payload.modules || [], pathname);
    // A page not covered by ANY permission module isn't under access control —
    // show it (don't hide pages the catalog simply doesn't map). Only pages that
    // ARE mapped to a module are gated by that module's view permission.
    if (!module) return true;

    const profile = (payload.roles || []).find((entry) => entry.role_id === role);
    if (!profile) return false; // mapped/controlled page + unknown role → deny
    return Boolean(profile.permissions?.[module.key]?.view);
}

export function filterNavItemsByPermissions<T extends { path?: string; children?: T[] }>(
    items: T[],
    payload: AccessPermissionsPayload | null,
    role: UserRole | undefined
): T[] {
    // super_admin / student aren't filtered here; the institute OWNER sees all.
    if (!role || !institutePortalRoles.has(role) || ownerRoles.has(role)) return items;
    // Any other institute role with an unknown catalog gets NOTHING (fail
    // closed) instead of the whole menu — this is the leak fix.
    if (!payload) return [];

    return items
        .map((item) => {
            if (item.path) {
                return canRoleAccessPath(payload, role, item.path) ? item : null;
            }

            if (item.children?.length) {
                const visibleChildren = filterNavItemsByPermissions(item.children, payload, role);
                return visibleChildren.length ? { ...item, children: visibleChildren } : null;
            }

            return item;
        })
        .filter(Boolean) as T[];
}

export function getFirstAccessiblePath(items: Array<{ path?: string; children?: Array<{ path?: string }> | any[] }>) {
    for (const item of items) {
        if (item.path) return item.path;
        for (const child of item.children || []) {
            if (child.path) return child.path;
        }
    }
    return '/admin/institute-dashboard';
}
